/**
 * Admin Affiliate Management Page
 * Overview and management of the entire affiliate system
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';

interface PlatformStats {
  total_partnerships: number;
  active_partnerships: number;
  total_earnings: number;
  pending_payouts: number;
  confirmed_payouts: number;
  paid_payouts: number;
  total_clicks: number;
  total_orders: number;
  conversion_rate: number;
}

interface CreatorPerformance {
  creator_id: string;
  creator_name: string;
  total_clicks: number;
  total_orders: number;
  total_earnings: number;
  conversion_rate: number;
}

interface BrandPerformance {
  brand_id: string;
  brand_name: string;
  total_partnerships: number;
  total_products_configured: number;
  total_commissions_paid: number;
  total_sales_generated: number;
}

interface PendingPayout {
  id: string;
  creator_name: string;
  amount: number;
  order_count: number;
}

export default function AffiliateManagement() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [topCreators, setTopCreators] = useState<CreatorPerformance[]>([]);
  const [topBrands, setTopBrands] = useState<BrandPerformance[]>([]);
  const [pendingPayouts, setPendingPayouts] = useState<PendingPayout[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [creatorPage, setCreatorPage] = useState(0);
  const [creatorRowsPerPage, setCreatorRowsPerPage] = useState(10);
  const [brandPage, setBrandPage] = useState(0);
  const [brandRowsPerPage, setBrandRowsPerPage] = useState(10);
  const [payoutPage, setPayoutPage] = useState(0);
  const [payoutRowsPerPage, setPayoutRowsPerPage] = useState(10);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPlatformStats(),
        loadTopCreators(),
        loadTopBrands(),
        loadPendingPayouts(),
      ]);
    } catch (err: any) {
      setError('데이터를 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPlatformStats = async () => {
    try {
      // Get partnerships count
      const { data: partnerships, error: partError } = await supabase
        .from('affiliate_partnerships')
        .select('status');

      if (partError) throw partError;

      // Get earnings
      const { data: earnings, error: earnError } = await supabase
        .from('affiliate_earnings')
        .select('commission_amount, status');

      if (earnError) throw earnError;

      // Get clicks
      const { data: clicks, error: clickError } = await supabase
        .from('affiliate_clicks')
        .select('id');

      if (clickError) throw clickError;

      // Get orders with attributions
      const { data: orders, error: orderError } = await supabase
        .from('affiliate_attributions')
        .select('id');

      if (orderError) throw orderError;

      const stats: PlatformStats = {
        total_partnerships: partnerships?.length || 0,
        active_partnerships: partnerships?.filter((p) => p.status === 'active').length || 0,
        total_earnings: 0,
        pending_payouts: 0,
        confirmed_payouts: 0,
        paid_payouts: 0,
        total_clicks: clicks?.length || 0,
        total_orders: orders?.length || 0,
        conversion_rate: 0,
      };

      (earnings || []).forEach((e) => {
        stats.total_earnings += e.commission_amount;
        if (e.status === 'pending') stats.pending_payouts += e.commission_amount;
        if (e.status === 'confirmed') stats.confirmed_payouts += e.commission_amount;
        if (e.status === 'paid') stats.paid_payouts += e.commission_amount;
      });

      if (stats.total_clicks > 0) {
        stats.conversion_rate = (stats.total_orders / stats.total_clicks) * 100;
      }

      setStats(stats);
    } catch (err: any) {
      console.error('Error loading platform stats:', err);
    }
  };

  const loadTopCreators = async () => {
    try {
      const { data: clicksData, error: clicksError } = await supabase
        .from('affiliate_clicks')
        .select('creator_id');

      if (clicksError) throw clicksError;

      const { data: earningsData, error: earnError } = await supabase
        .from('affiliate_earnings')
        .select(
          `
          creator_id,
          commission_amount,
          status,
          creator:seller_profile!affiliate_earnings_creator_id_fkey(store_name)
        `
        )
        .in('status', ['confirmed', 'paid']);

      if (earnError) throw earnError;

      const { data: ordersData, error: orderError } = await supabase
        .from('affiliate_attributions')
        .select('creator_id');

      if (orderError) throw orderError;

      // Aggregate by creator
      const creatorMap: Record<string, CreatorPerformance> = {};

      // Count clicks
      (clicksData || []).forEach((click) => {
        if (!creatorMap[click.creator_id]) {
          creatorMap[click.creator_id] = {
            creator_id: click.creator_id,
            creator_name: '',
            total_clicks: 0,
            total_orders: 0,
            total_earnings: 0,
            conversion_rate: 0,
          };
        }
        creatorMap[click.creator_id].total_clicks++;
      });

      // Count orders and earnings
      (earningsData || []).forEach((earning: any) => {
        if (!creatorMap[earning.creator_id]) {
          creatorMap[earning.creator_id] = {
            creator_id: earning.creator_id,
            creator_name: earning.creator?.store_name || 'Unknown',
            total_clicks: 0,
            total_orders: 0,
            total_earnings: 0,
            conversion_rate: 0,
          };
        }
        creatorMap[earning.creator_id].creator_name = earning.creator?.store_name || 'Unknown';
        creatorMap[earning.creator_id].total_orders++;
        creatorMap[earning.creator_id].total_earnings += earning.commission_amount;
      });

      // Calculate conversion rates
      const creators = Object.values(creatorMap).map((c) => ({
        ...c,
        conversion_rate: c.total_clicks > 0 ? (c.total_orders / c.total_clicks) * 100 : 0,
      }));

      // Sort by earnings
      creators.sort((a, b) => b.total_earnings - a.total_earnings);

      setTopCreators(creators.slice(0, 10));
    } catch (err: any) {
      console.error('Error loading top creators:', err);
    }
  };

  const loadTopBrands = async () => {
    try {
      const { data: partnerships, error: partError } = await supabase
        .from('affiliate_partnerships')
        .select('brand_id');

      if (partError) throw partError;

      const { data: settings, error: settingsError } = await supabase
        .from('product_affiliate_settings')
        .select('brand_id, product_id');

      if (settingsError) throw settingsError;

      const { data: earnings, error: earnError } = await supabase
        .from('affiliate_earnings')
        .select(
          `
          brand_id,
          commission_amount,
          order_amount,
          brand:seller_profile!affiliate_earnings_brand_id_fkey(store_name)
        `
        )
        .in('status', ['confirmed', 'paid']);

      if (earnError) throw earnError;

      // Aggregate by brand
      const brandMap: Record<string, BrandPerformance> = {};

      (partnerships || []).forEach((p) => {
        if (!brandMap[p.brand_id]) {
          brandMap[p.brand_id] = {
            brand_id: p.brand_id,
            brand_name: '',
            total_partnerships: 0,
            total_products_configured: 0,
            total_commissions_paid: 0,
            total_sales_generated: 0,
          };
        }
        brandMap[p.brand_id].total_partnerships++;
      });

      (settings || []).forEach((s) => {
        if (brandMap[s.brand_id]) {
          brandMap[s.brand_id].total_products_configured++;
        }
      });

      (earnings || []).forEach((e: any) => {
        if (!brandMap[e.brand_id]) {
          brandMap[e.brand_id] = {
            brand_id: e.brand_id,
            brand_name: e.brand?.store_name || 'Unknown',
            total_partnerships: 0,
            total_products_configured: 0,
            total_commissions_paid: 0,
            total_sales_generated: 0,
          };
        }
        brandMap[e.brand_id].brand_name = e.brand?.store_name || 'Unknown';
        brandMap[e.brand_id].total_commissions_paid += e.commission_amount;
        brandMap[e.brand_id].total_sales_generated += e.order_amount;
      });

      const brands = Object.values(brandMap);
      brands.sort((a, b) => b.total_sales_generated - a.total_sales_generated);

      setTopBrands(brands.slice(0, 10));
    } catch (err: any) {
      console.error('Error loading top brands:', err);
    }
  };

  const loadPendingPayouts = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_earnings')
        .select(
          `
          creator_id,
          commission_amount,
          creator:seller_profile!affiliate_earnings_creator_id_fkey(store_name)
        `
        )
        .eq('status', 'confirmed');

      if (error) throw error;

      // Group by creator
      const payoutMap: Record<string, { creator_name: string; amount: number; count: number }> = {};

      (data || []).forEach((e: any) => {
        if (!payoutMap[e.creator_id]) {
          payoutMap[e.creator_id] = {
            creator_name: e.creator?.store_name || 'Unknown',
            amount: 0,
            count: 0,
          };
        }
        payoutMap[e.creator_id].amount += e.commission_amount;
        payoutMap[e.creator_id].count++;
      });

      const payouts = Object.entries(payoutMap).map(([id, data]) => ({
        id,
        creator_name: data.creator_name,
        amount: data.amount,
        order_count: data.count,
      }));

      payouts.sort((a, b) => b.amount - a.amount);

      setPendingPayouts(payouts);
    } catch (err: any) {
      console.error('Error loading pending payouts:', err);
    }
  };

  // Pagination handlers
  const handleCreatorPageChange = (event: unknown, newPage: number) => {
    setCreatorPage(newPage);
  };

  const handleCreatorRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreatorRowsPerPage(parseInt(event.target.value, 10));
    setCreatorPage(0);
  };

  const handleBrandPageChange = (event: unknown, newPage: number) => {
    setBrandPage(newPage);
  };

  const handleBrandRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBrandRowsPerPage(parseInt(event.target.value, 10));
    setBrandPage(0);
  };

  const handlePayoutPageChange = (event: unknown, newPage: number) => {
    setPayoutPage(newPage);
  };

  const handlePayoutRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPayoutRowsPerPage(parseInt(event.target.value, 10));
    setPayoutPage(0);
  };

  // Paginated data
  const paginatedCreators = topCreators.slice(
    creatorPage * creatorRowsPerPage,
    creatorPage * creatorRowsPerPage + creatorRowsPerPage
  );

  const paginatedBrands = topBrands.slice(
    brandPage * brandRowsPerPage,
    brandPage * brandRowsPerPage + brandRowsPerPage
  );

  const paginatedPayouts = pendingPayouts.slice(
    payoutPage * payoutRowsPerPage,
    payoutPage * payoutRowsPerPage + payoutRowsPerPage
  );

  const handleMarkAsPaid = async (creatorId: string) => {
    if (!window.confirm('이 크리에이터의 확정된 수익을 모두 지급 완료로 표시하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('affiliate_earnings')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
        })
        .eq('creator_id', creatorId)
        .eq('status', 'confirmed');

      if (error) throw error;

      setSuccess('지급 완료 처리되었습니다');
      loadAllData();
    } catch (err: any) {
      setError('지급 처리 실패: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        어필리에이트 관리
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Platform Stats */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    활성 파트너십
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats.active_partnerships}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  총 {stats.total_partnerships}개
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    총 커미션
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {stats.total_earnings.toLocaleString()}원
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  지급 완료: {stats.paid_payouts.toLocaleString()}원
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    전환율
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {stats.conversion_rate.toFixed(2)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  클릭: {stats.total_clicks.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ShoppingCartIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    어필리에이트 주문
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold">
                  {stats.total_orders.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  확정 대기: {stats.confirmed_payouts.toLocaleString()}원
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="상위 크리에이터" />
          <Tab label="상위 브랜드" />
          <Tab label={`정산 대기 (${pendingPayouts.length})`} />
        </Tabs>
      </Paper>

      {/* Top Creators */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              상위 크리에이터
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>순위</TableCell>
                    <TableCell>크리에이터</TableCell>
                    <TableCell align="right">클릭수</TableCell>
                    <TableCell align="right">주문수</TableCell>
                    <TableCell align="right">전환율</TableCell>
                    <TableCell align="right">총 수익</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCreators.map((creator, index) => (
                    <TableRow key={creator.creator_id}>
                      <TableCell>#{creatorPage * creatorRowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">{creator.creator_name}</Typography>
                      </TableCell>
                      <TableCell align="right">{creator.total_clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{creator.total_orders.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${creator.conversion_rate.toFixed(1)}%`} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{creator.total_earnings.toLocaleString()}원</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={topCreators.length}
              page={creatorPage}
              onPageChange={handleCreatorPageChange}
              rowsPerPage={creatorRowsPerPage}
              onRowsPerPageChange={handleCreatorRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="페이지당 행:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </CardContent>
        </Card>
      )}

      {/* Top Brands */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              상위 브랜드
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>순위</TableCell>
                    <TableCell>브랜드</TableCell>
                    <TableCell align="right">파트너수</TableCell>
                    <TableCell align="right">설정 제품수</TableCell>
                    <TableCell align="right">총 매출</TableCell>
                    <TableCell align="right">지급 커미션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBrands.map((brand, index) => (
                    <TableRow key={brand.brand_id}>
                      <TableCell>#{brandPage * brandRowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">{brand.brand_name}</Typography>
                      </TableCell>
                      <TableCell align="right">{brand.total_partnerships}</TableCell>
                      <TableCell align="right">{brand.total_products_configured}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{brand.total_sales_generated.toLocaleString()}원</Typography>
                      </TableCell>
                      <TableCell align="right">{brand.total_commissions_paid.toLocaleString()}원</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={topBrands.length}
              page={brandPage}
              onPageChange={handleBrandPageChange}
              rowsPerPage={brandRowsPerPage}
              onRowsPerPageChange={handleBrandRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="페이지당 행:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </CardContent>
        </Card>
      )}

      {/* Pending Payouts */}
      {tabValue === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              정산 대기 목록
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>크리에이터</TableCell>
                    <TableCell align="right">주문 건수</TableCell>
                    <TableCell align="right">정산 금액</TableCell>
                    <TableCell align="right">액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingPayouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">정산 대기 중인 항목이 없습니다</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPayouts.map((payout) => (
                      <TableRow key={payout.id}>
                        <TableCell>
                          <Typography fontWeight="bold">{payout.creator_name}</Typography>
                        </TableCell>
                        <TableCell align="right">{payout.order_count}건</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            {payout.amount.toLocaleString()}원
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            onClick={() => handleMarkAsPaid(payout.id)}
                          >
                            지급 완료
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pendingPayouts.length}
              page={payoutPage}
              onPageChange={handlePayoutPageChange}
              rowsPerPage={payoutRowsPerPage}
              onRowsPerPageChange={handlePayoutRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="페이지당 행:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
